import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Form } from "reactstrap";
import { useSelector } from "react-redux";
import {
    Award, Calendar, Camera, Gift, Grid, MapPin, Percent, Save, Shield, Sliders, Tag,
} from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import Loader from "../../shared/Loader";
import MultiSelectField from "../common/MultiSelectField";
import { errorToast, successToast } from "../../shared/Toast";
import { C, reportingGlobalCSS, selectStyles } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";
import ConfigAuditLog from "./ConfigAuditLog";
import DiscountRulesEditor, { validateDiscountRules, deadRuleReason } from "./DiscountRulesEditor";
import PillTabs from "../common/PillTabs";
import {
    Cols, Field, SaveBar, Section, SwitchField,
    hintStyle, inputStyle, primaryBtnStyle, secondaryBtnStyle, withDisabled,
} from "../common/FormKit";
import {
    getMembershipType, createMembershipType, updateMembershipType,
} from "../../utils/membershipApi";
import { getCategoryListing, getProductListing, fetchBranches } from "../../utils/apiCalls";
import { ORDER_NATURES, ORDER_TYPES, LOYALTY_POLICIES, CONFIG_ENTITIES } from "./membershipConstants";

/**
 * Create or edit a membership tier.
 *
 * ONE component serving both routes, rather than the add-loyalty.js / edit-loyalty.js pair
 * this repo has elsewhere. Those two are near-duplicates of about 1,200 lines each, and
 * they have already drifted — a field added to one and forgotten in the other is invisible
 * until someone edits a programme and loses a setting. A tier has 22 editable fields and
 * three eligibility lists; duplicating that is asking for the same drift. `params.id`
 * decides which mode we are in, and that is the only difference between them.
 *
 * All three layers the plan calls for are exposed here: the tier's own config, its
 * eligibility lists, and its channel restrictions. Per-member overrides live on the member
 * profile, since they belong to a person rather than to the tier.
 *
 * Layout: the Digital Signage "Configure screen" look (common/FormKit) — titled sections in a
 * two-column grid, uppercase field labels, and a sticky save bar — split into tabs styled like
 * the Product Recommendations page (common/PillTabs).
 */

/** Two columns of sections on a wide screen, one on a narrow one. */
const sectionGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 16,
    alignItems: "start",
};

/** react-select, themed to match the inputs, with its menu above the section borders. */
const selectProps = {
    styles: selectStyles,
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
};

/** Field defaults mirror the API's column defaults, so a straight save changes nothing. */
const EMPTY = {
    name: "",
    arabicName: "",
    code: "",
    discountType: "percentage",
    discountValue: "",
    cardColour: "#009689",
    displayOrder: 0,
    effectiveDate: "",
    expiryDate: "",
    defaultValidityMonths: "",
    maxOrderValue: "",
    maxDailyOrders: "",
    maxMonthlyOrders: "",
    maxDailyDiscountValue: "",
    applyToModifiers: true,
    combinableWithCoupons: true,
    applyToAllCategories: true,
    memberLoyaltyPolicy: "when_no_discount",
    requiresPhoto: false,
    allowedOrderNatures: [],
    allowedOrderTypes: [],
    eligibleCategoryIds: [],
    excludedProductIds: [],
    eligibleBranchIds: [],
    // Per-category / per-product rates. The tier's discount above is the default.
    discountRules: [],
};

/** `<input type="date">` wants YYYY-MM-DD; the API returns an ISO timestamp. */
const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

/** "" is how an emptied number field arrives, and it must reach the API as null, not 0. */
const toNullableNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

/** The API's tier, as the form holds it. Used for the loaded state and for Discard. */
const formFromType = (data) => ({
    ...EMPTY,
    ...data,
    discountValue: data.discountValue ?? "",
    displayOrder: data.displayOrder ?? 0,
    effectiveDate: toDateInput(data.effectiveDate),
    expiryDate: toDateInput(data.expiryDate),
    defaultValidityMonths: data.defaultValidityMonths ?? "",
    maxOrderValue: data.maxOrderValue ?? "",
    maxDailyOrders: data.maxDailyOrders ?? "",
    maxMonthlyOrders: data.maxMonthlyOrders ?? "",
    maxDailyDiscountValue: data.maxDailyDiscountValue ?? "",
    allowedOrderNatures: data.allowedOrderNatures || [],
    allowedOrderTypes: data.allowedOrderTypes || [],
    // The API returns the joined rows; the form works in ids.
    eligibleCategoryIds: (data.eligibleCategories || []).map((c) => c.id),
    excludedProductIds: (data.excludedProducts || []).map((p) => p.id),
    eligibleBranchIds: (data.eligibleBranches || []).map((b) => b.id),
    discountRules: (data.discountRules || []).map((r) => ({
        scope: r.scope,
        targetId: Number(r.targetId),
        targetName: r.targetName || null,
        discountType: r.discountType,
        discountValue: r.discountValue ?? "",
    })),
});

const MembershipTypeForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});

    const merchantId = userType === "merchant"
        ? loggedInMerchantId
        : (reduxSelectedMerchant && reduxSelectedMerchant !== "all") ? reduxSelectedMerchant : "";

    const [form, setForm] = useState(EMPTY);
    // What the server holds (or the blank form, on create), so the save bar can say whether
    // anything is unsaved and Discard knows what to go back to.
    const [savedForm, setSavedForm] = useState(EMPTY);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSaving, setIsSaving] = useState(false);
    const [activeMembers, setActiveMembers] = useState(0);
    /**
     * The revision this form loaded, echoed back on save.
     *
     * Not part of `form` because it is not a field an admin edits — and if it lived there,
     * a stray `set({ ... })` could overwrite it and quietly disable the guard.
     */
    const [revision, setRevision] = useState(null);

    // Which section is on screen. Every section's values live in `form`, so switching tab
    // loses nothing, and the payload always carries every field whichever tab is open.
    const [tab, setTab] = useState("details");

    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [branches, setBranches] = useState([]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    // ── Load the catalogue for the eligibility pickers ────────────────────────────────
    useEffect(() => {
        if (!merchantId) return;
        (async () => {
            const [cats, prods, brs] = await Promise.all([
                getCategoryListing(merchantId),
                // The full list, not the first ten. react-select filters the options it is
                // given, so capping the array — as add-loyalty.js does — silently makes
                // everything past the tenth product unsearchable.
                getProductListing(merchantId),
                fetchBranches({ merchantID: merchantId }),
            ]);
            setCategories(Array.isArray(cats) ? cats : cats?.data || []);
            setProducts(Array.isArray(prods) ? prods : prods?.data || []);
            setBranches(brs?.data || []);
        })();
    }, [merchantId]);

    // ── Load the tier being edited ───────────────────────────────────────────────────
    const loadType = useCallback(async () => {
        if (!isEdit) return;
        setIsLoading(true);
        const data = await getMembershipType(id);
        setIsLoading(false);
        if (!data) { navigate("/membership/types"); return; }

        setActiveMembers(Number(data.activeMembers) || 0);
        setRevision(data.revision ?? null);
        const loaded = formFromType(data);
        setForm(loaded);
        setSavedForm(loaded);
    }, [id, isEdit, navigate]);

    useEffect(() => { loadType(); }, [loadType]);

    const categoryOptions = useMemo(
        () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
        [categories]
    );
    const productOptions = useMemo(
        () => (products || []).map((p) => ({ value: p.id, label: p.title })),
        [products]
    );
    // { productId: CategoryId }, so a product rate whose category is outside a restricted
    // list is caught here rather than by the API's 400 after save.
    const productCategoryById = useMemo(
        () => Object.fromEntries((products || []).map((p) => [p.id, p.CategoryId ?? null])),
        [products]
    );
    const branchOptions = useMemo(
        () => (branches || []).map((b) => ({ value: b.id, label: b.name })),
        [branches]
    );

    const pick = (options, ids) => options.filter((o) => (ids || []).includes(o.value));

    // The context every rate check needs. One object, so the save check, the tab's alert dot
    // and the per-row warnings all judge a rate the same way.
    const ruleContext = {
        excludedProductIds: form.excludedProductIds,
        applyToAllCategories: form.applyToAllCategories,
        eligibleCategoryIds: form.eligibleCategoryIds,
        productCategoryById,
    };
    const deadRuleCount = (form.discountRules || []).filter((r) => deadRuleReason(r, ruleContext)).length;

    const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);

    /**
     * Client-side checks for the mistakes that are cheap to catch here. The API validates
     * all of these again — these exist to save a round trip, not to be the only guard.
     *
     * Each problem names the tab its field lives on, so a failed save opens that tab
     * instead of leaving the admin hunting for a field they cannot see.
     */
    const validate = () => {
        const fail = (tabKey, message) => ({ tab: tabKey, message });

        if (!merchantId) return fail(tab, "Select a merchant first.");
        if (!form.name.trim()) return fail("details", "Tier name is required.");

        const value = Number(form.discountValue);
        // Zero is allowed only when rates exist: "Drinks 20%, nothing else" is a real tier.
        const hasRules = (form.discountRules || []).length > 0;
        if (!Number.isFinite(value) || value < 0 || (value === 0 && !hasRules)) {
            return fail("discount", hasRules ? "Enter a default discount of zero or more." : "Enter a discount greater than zero.");
        }
        if (form.discountType === "percentage" && value > 100) {
            return fail("discount", "A percentage discount cannot be more than 100%.");
        }

        if (form.effectiveDate && form.expiryDate && form.effectiveDate > form.expiryDate) {
            return fail("details", "The tier's end date cannot be before its start date.");
        }
        if (form.applyToAllCategories === false && form.eligibleCategoryIds.length === 0) {
            return fail("eligibility", "Pick at least one eligible category, or switch \"All categories\" back on.");
        }
        const ruleProblem = validateDiscountRules(form.discountRules, ruleContext);
        return ruleProblem ? fail("rates", ruleProblem) : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const problem = validate();
        if (problem) {
            setTab(problem.tab);
            errorToast(problem.message);
            return;
        }

        const payload = {
            merchantId: Number(merchantId),
            name: form.name.trim(),
            arabicName: form.arabicName || null,
            code: form.code || null,
            discountType: form.discountType,
            discountValue: Number(form.discountValue),
            cardColour: form.cardColour || null,
            displayOrder: Number(form.displayOrder) || 0,
            effectiveDate: form.effectiveDate || null,
            expiryDate: form.expiryDate || null,
            defaultValidityMonths: toNullableNumber(form.defaultValidityMonths),
            maxOrderValue: toNullableNumber(form.maxOrderValue),
            maxDailyOrders: toNullableNumber(form.maxDailyOrders),
            maxMonthlyOrders: toNullableNumber(form.maxMonthlyOrders),
            maxDailyDiscountValue: toNullableNumber(form.maxDailyDiscountValue),
            applyToModifiers: form.applyToModifiers,
            combinableWithCoupons: form.combinableWithCoupons,
            applyToAllCategories: form.applyToAllCategories,
            memberLoyaltyPolicy: form.memberLoyaltyPolicy,
            requiresPhoto: form.requiresPhoto,
            allowedOrderNatures: form.allowedOrderNatures,
            allowedOrderTypes: form.allowedOrderTypes,
            // Always sent, so the form owns these lists outright. An empty array is a real
            // instruction to the API — "clear it" — which restores open-by-default.
            eligibleCategoryIds: form.applyToAllCategories ? [] : form.eligibleCategoryIds,
            excludedProductIds: form.excludedProductIds,
            eligibleBranchIds: form.eligibleBranchIds,
            // Always sent, like the lists above: [] clears every rate.
            discountRules: (form.discountRules || []).map((r) => ({
                scope: r.scope,
                targetId: Number(r.targetId),
                discountType: r.discountType,
                discountValue: Number(r.discountValue),
            })),
            // The revision this form loaded. The API refuses the save with a 409 if
            // somebody else committed first, rather than letting this payload — which
            // carries EVERY field, including ones nobody here touched — revert them.
            ...(isEdit && revision !== null ? { revision } : {}),
        };

        setIsSaving(true);
        const saved = isEdit
            ? await updateMembershipType(id, payload)
            : await createMembershipType(payload);
        setIsSaving(false);

        if (saved?.conflict) {
            // Reload rather than leaving the admin's edits on screen over stale values:
            // they need to see what the other person changed before deciding what to
            // re-apply. The toast has already told them why.
            loadType();
            return;
        }

        if (saved) {
            successToast(isEdit ? "Tier updated" : "Tier created");
            navigate("/membership/types");
        }
    };

    if (isLoading) return <Loader />;

    const isPercentage = form.discountType === "percentage";
    const saveDisabled = isSaving || !merchantId;

    return (
        <Fragment>
            <Breadcrumb title={isEdit ? "Edit Membership Tier" : "Create Membership Tier"} parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    {/* ── Header ──────────────────────────────────────── */}
                    <RCard style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: `linear-gradient(135deg, ${form.cardColour || C.coral}, #00bfa5)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                                    flexShrink: 0,
                                }}
                            >
                                <Award size={22} color={C.white} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>
                                    {isEdit ? form.name || "Membership Tier" : "New Membership Tier"}
                                </div>
                                <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
                                    {isEdit ? (
                                        <>
                                            {activeMembers} active member{activeMembers === 1 ? "" : "s"} on this tier.
                                            Changes apply to future orders only — past orders keep the terms they were placed under.
                                        </>
                                    ) : (
                                        "A new tier is created inactive. Activate it from the list when it is ready."
                                    )}
                                </div>
                            </div>
                        </div>
                    </RCard>

                    {!merchantId && (
                        <div style={{ padding: 16, marginBottom: 16, borderRadius: 12, background: C.orangeLight, color: C.text, fontSize: 13 }}>
                            Select a merchant from the header before creating a tier.
                        </div>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <PillTabs
                            ariaLabel="Tier sections"
                            value={tab}
                            onChange={setTab}
                            tabs={[
                                { key: "details", label: "Details" },
                                { key: "discount", label: "Discount" },
                                {
                                    key: "rates",
                                    label: "Category & product rates",
                                    badge: (form.discountRules || []).length,
                                    alert: deadRuleCount > 0,
                                },
                                { key: "eligibility", label: "Where it applies" },
                                { key: "limits", label: "Usage limits" },
                                { key: "loyalty", label: "Loyalty" },
                                // A tier that does not exist yet has no history.
                                ...(isEdit ? [{ key: "history", label: "History" }] : []),
                            ]}
                        />

                        {/* ── Details ─────────────────────────────────── */}
                        {tab === "details" && (
                            <div style={sectionGrid}>
                                <Section icon={Award} title="Tier identity" subtitle="How the tier is named and shown on the member's card.">
                                    <Field label="Name" required>
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => set({ name: e.target.value })}
                                            placeholder="e.g. Ambassador"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Cols>
                                        <Field label="Arabic name" hint="Shown on the card in the app for Arabic users.">
                                            <input
                                                type="text"
                                                value={form.arabicName || ""}
                                                onChange={(e) => set({ arabicName: e.target.value })}
                                                placeholder="سفير"
                                                dir="rtl"
                                                style={inputStyle}
                                            />
                                        </Field>
                                        <Field label="Code" hint="Short slug for reports. Unique within this merchant.">
                                            <input
                                                type="text"
                                                value={form.code || ""}
                                                onChange={(e) => set({ code: e.target.value.toUpperCase() })}
                                                placeholder="AMB"
                                                style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: 1 }}
                                            />
                                        </Field>
                                    </Cols>
                                    <Cols>
                                        <Field label="Card colour">
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <input
                                                    type="color"
                                                    value={form.cardColour || "#009689"}
                                                    onChange={(e) => set({ cardColour: e.target.value })}
                                                    aria-label="Card colour picker"
                                                    style={{ ...inputStyle, width: 48, padding: 3, height: 38, flexShrink: 0, cursor: "pointer" }}
                                                />
                                                <input
                                                    type="text"
                                                    value={form.cardColour || ""}
                                                    onChange={(e) => set({ cardColour: e.target.value })}
                                                    placeholder="#009689"
                                                    style={{ ...inputStyle, fontFamily: "monospace" }}
                                                />
                                            </div>
                                        </Field>
                                        <Field label="Display order" hint="Lower numbers appear first in lists.">
                                            <input
                                                type="number"
                                                value={form.displayOrder}
                                                onChange={(e) => set({ displayOrder: e.target.value })}
                                                style={inputStyle}
                                            />
                                        </Field>
                                    </Cols>
                                </Section>

                                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    <Section icon={Calendar} title="Dates" subtitle="When the tier runs, and how long a new card lasts.">
                                        <Cols>
                                            <Field label="Tier starts">
                                                <input
                                                    type="date"
                                                    value={form.effectiveDate}
                                                    onChange={(e) => set({ effectiveDate: e.target.value })}
                                                    style={inputStyle}
                                                />
                                            </Field>
                                            <Field label="Tier ends">
                                                <input
                                                    type="date"
                                                    value={form.expiryDate}
                                                    onChange={(e) => set({ expiryDate: e.target.value })}
                                                    style={inputStyle}
                                                />
                                            </Field>
                                        </Cols>
                                        <span style={{ ...hintStyle, marginTop: -6 }}>
                                            These two are the <strong>tier's own</strong> lifespan — a seasonal tier
                                            opens and closes by itself. Outside the window, cards stay active but
                                            stop discounting. Leave both blank for a permanent tier.
                                        </span>
                                        <Field
                                            label="Default card validity (months)"
                                            hint={
                                                <span style={hintStyle}>
                                                    A different thing: this only fills in the expiry date of a
                                                    <strong> newly assigned </strong> card. Existing cards are untouched.
                                                </span>
                                            }
                                        >
                                            <input
                                                type="number"
                                                value={form.defaultValidityMonths}
                                                onChange={(e) => set({ defaultValidityMonths: e.target.value })}
                                                placeholder="Blank = cards never expire"
                                                style={inputStyle}
                                            />
                                        </Field>
                                    </Section>

                                    <Section icon={Camera} title="Verification" subtitle="Whether a card needs a checked photo first.">
                                        <SwitchField
                                            label="Require a member photo"
                                            checked={form.requiresPhoto}
                                            onChange={(v) => set({ requiresPhoto: v })}
                                            hint="When on, a card whose photo is not verified gives no discount."
                                        />
                                    </Section>
                                </div>
                            </div>
                        )}

                        {/* ── Discount ────────────────────────────────── */}
                        {tab === "discount" && (
                            <div style={sectionGrid}>
                                <Section icon={Percent} title="Default discount" subtitle="What every qualifying item gets, unless it has its own rate.">
                                    <Cols>
                                        <Field label="Type">
                                            <select
                                                value={form.discountType}
                                                onChange={(e) => set({ discountType: e.target.value })}
                                                style={inputStyle}
                                            >
                                                <option value="percentage">Percentage off</option>
                                                <option value="amount">Fixed amount off (SAR)</option>
                                            </select>
                                        </Field>
                                        <Field label={isPercentage ? "Percentage" : "Amount (SAR)"} required>
                                            <input
                                                type="number"
                                                step={isPercentage ? "1" : "0.01"}
                                                max={isPercentage ? 100 : undefined}
                                                value={form.discountValue}
                                                onChange={(e) => set({ discountValue: e.target.value })}
                                                placeholder={isPercentage ? "15" : "20.00"}
                                                style={inputStyle}
                                            />
                                        </Field>
                                    </Cols>
                                    <span style={{ ...hintStyle, marginTop: -6 }}>
                                        {isPercentage
                                            ? "Applied to the qualifying items only."
                                            : "Taken off the order once, never more than the qualifying basket — it cannot become store credit."}
                                        {" "}This is the default. Items given their own rate on the Category &amp; product rates tab are priced by that rate instead.
                                    </span>
                                </Section>

                                <Section icon={Sliders} title="How it combines" subtitle="Add-ons, and coupons on the same order.">
                                    <SwitchField
                                        label="Include modifiers"
                                        checked={form.applyToModifiers}
                                        onChange={(v) => set({ applyToModifiers: v })}
                                        hint="Off means the discount covers base prices only; the member pays full price for extra shots, syrups and the like."
                                    />
                                    <SwitchField
                                        label="Can be combined with coupons"
                                        checked={form.combinableWithCoupons}
                                        onChange={(v) => set({ combinableWithCoupons: v })}
                                        hint="When on, the membership discount applies first and the coupon applies to what is left. When off, a member using a coupon gets no membership discount on that order."
                                    />
                                </Section>
                            </div>
                        )}

                        {/* ── Rates by category / product ─────────────── */}
                        {tab === "rates" && (
                            <Section
                                icon={Tag}
                                title="Rates by category or product"
                                subtitle="Rates that replace the default for chosen items."
                                aside={deadRuleCount > 0 ? (
                                    <span style={{ fontSize: 12, fontWeight: 600, color: C.orange }}>
                                        {deadRuleCount} rate{deadRuleCount === 1 ? "" : "s"} would never apply
                                    </span>
                                ) : null}
                            >
                                <DiscountRulesEditor
                                    rules={form.discountRules}
                                    onChange={(discountRules) => set({ discountRules })}
                                    categoryOptions={categoryOptions}
                                    productOptions={productOptions}
                                    isDisabled={!merchantId}
                                    excludedProductIds={form.excludedProductIds}
                                    applyToAllCategories={form.applyToAllCategories}
                                    eligibleCategoryIds={form.eligibleCategoryIds}
                                    productCategoryById={productCategoryById}
                                    defaultLabel={
                                        isPercentage
                                            ? `${form.discountValue || 0}% off`
                                            : `SAR ${form.discountValue || 0} off the order`
                                    }
                                />
                            </Section>
                        )}

                        {/* ── Where it applies ────────────────────────── */}
                        {tab === "eligibility" && (
                            <>
                                <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
                                    Every list here is open by default: leaving one empty means
                                    <strong> no restriction</strong>, not "nothing".
                                </p>
                                <div style={sectionGrid}>
                                    <Section icon={Grid} title="Categories & products" subtitle="Which items qualify for the discount.">
                                        <SwitchField
                                            label="All categories"
                                            checked={form.applyToAllCategories}
                                            onChange={(v) => set({ applyToAllCategories: v })}
                                            hint="Turn off to limit the discount to chosen categories."
                                        />
                                        {!form.applyToAllCategories && (
                                            <Field label="Eligible categories">
                                                <MultiSelectField
                                                    {...selectProps}
                                                    options={categoryOptions}
                                                    value={pick(categoryOptions, form.eligibleCategoryIds)}
                                                    onChange={(sel) => set({ eligibleCategoryIds: (sel || []).map((s) => s.value) })}
                                                    placeholder="Search & select categories..."
                                                    isDisabled={!merchantId}
                                                />
                                            </Field>
                                        )}
                                        <Field
                                            label="Excluded products"
                                            hint="These never receive the discount, even if their category is eligible. The app marks them in the basket so the member can see why."
                                        >
                                            <MultiSelectField
                                                {...selectProps}
                                                options={productOptions}
                                                value={pick(productOptions, form.excludedProductIds)}
                                                onChange={(sel) => set({ excludedProductIds: (sel || []).map((s) => s.value) })}
                                                placeholder="Search & select products to exclude..."
                                                isDisabled={!merchantId}
                                            />
                                        </Field>
                                    </Section>

                                    <Section icon={MapPin} title="Branches & channels" subtitle="Where, and how, the order is placed.">
                                        <Field label="Eligible branches">
                                            <MultiSelectField
                                                {...selectProps}
                                                options={branchOptions}
                                                value={pick(branchOptions, form.eligibleBranchIds)}
                                                onChange={(sel) => set({ eligibleBranchIds: (sel || []).map((s) => s.value) })}
                                                placeholder="Leave empty for all branches..."
                                                isDisabled={!merchantId}
                                            />
                                        </Field>
                                        <Field
                                            label="Order channels"
                                            hint='Where the order came from. Pick "POS (counter)" only, for a staff-only tier.'
                                        >
                                            <MultiSelectField
                                                {...selectProps}
                                                options={ORDER_NATURES}
                                                value={ORDER_NATURES.filter((o) => (form.allowedOrderNatures || []).includes(o.value))}
                                                onChange={(sel) => set({ allowedOrderNatures: (sel || []).map((s) => s.value) })}
                                                placeholder="Leave empty for all channels..."
                                            />
                                        </Field>
                                        <Field label="Fulfilment types" hint="How the order is collected — e.g. pickup but not delivery.">
                                            <MultiSelectField
                                                {...selectProps}
                                                options={ORDER_TYPES}
                                                value={ORDER_TYPES.filter((o) => (form.allowedOrderTypes || []).includes(o.value))}
                                                onChange={(sel) => set({ allowedOrderTypes: (sel || []).map((s) => s.value) })}
                                                placeholder="Leave empty for all types..."
                                            />
                                        </Field>
                                    </Section>
                                </div>
                            </>
                        )}

                        {/* ── Usage limits ────────────────────────────── */}
                        {tab === "limits" && (
                            <Section
                                icon={Shield}
                                title="Usage limits"
                                subtitle="Leave a field blank for no limit."
                            >
                                <span style={{ ...hintStyle, marginTop: -6 }}>
                                    Limits are counted in Riyadh time and are
                                    <strong> net of returns</strong> — a fully returned order releases the slot it used.
                                    A single member can be given different limits on their own profile.
                                </span>
                                <Cols min={220}>
                                    <Field label="Max order value (SAR)" hint="Baskets above this get no discount at all, rather than a capped one.">
                                        <input
                                            type="number" step="0.01"
                                            value={form.maxOrderValue}
                                            onChange={(e) => set({ maxOrderValue: e.target.value })}
                                            placeholder="No limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Discounted orders per day">
                                        <input
                                            type="number"
                                            value={form.maxDailyOrders}
                                            onChange={(e) => set({ maxDailyOrders: e.target.value })}
                                            placeholder="No limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Discounted orders per month">
                                        <input
                                            type="number"
                                            value={form.maxMonthlyOrders}
                                            onChange={(e) => set({ maxMonthlyOrders: e.target.value })}
                                            placeholder="No limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field
                                        label="Daily discount budget (SAR)"
                                        hint={
                                            <span style={hintStyle}>
                                                Unlike the others this <strong>trims</strong> rather than blocks: if only
                                                SAR 5 of budget is left, the member gets SAR 5 off instead of nothing.
                                            </span>
                                        }
                                    >
                                        <input
                                            type="number" step="0.01"
                                            value={form.maxDailyDiscountValue}
                                            onChange={(e) => set({ maxDailyDiscountValue: e.target.value })}
                                            placeholder="No limit"
                                            style={inputStyle}
                                        />
                                    </Field>
                                </Cols>
                            </Section>
                        )}

                        {/* ── Loyalty ─────────────────────────────────── */}
                        {tab === "loyalty" && (
                            <Section icon={Gift} title="Loyalty for members of this tier" subtitle="Whether a member also earns loyalty points or stamps.">
                                {/*
                                  One radio group, not a set of switches. The four values answer a
                                  single question and form a ladder from most restrictive to least;
                                  as separate booleans several of the combinations would contradict
                                  each other and nothing would stop an admin setting them.
                                */}
                                <span style={{ ...hintStyle, marginTop: -6 }}>
                                    A member can be in three situations on any order: the discount applied,
                                    the card worked but gave nothing (cap spent, item excluded, branch not
                                    covered), or the card was not working at all. This decides which of
                                    those earn.
                                </span>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {LOYALTY_POLICIES.map((p) => {
                                        const selected = form.memberLoyaltyPolicy === p.value;
                                        return (
                                            <label
                                                key={p.value}
                                                style={{
                                                    display: "flex", gap: 12, alignItems: "flex-start", margin: 0,
                                                    padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                                                    border: `1.5px solid ${selected ? C.coral : C.border}`,
                                                    background: selected ? C.coralLight : C.white,
                                                    transition: "all 0.15s",
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="memberLoyaltyPolicy"
                                                    value={p.value}
                                                    checked={selected}
                                                    onChange={() => set({ memberLoyaltyPolicy: p.value })}
                                                    style={{ marginTop: 3, accentColor: C.coral }}
                                                />
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: selected ? C.coral : C.text }}>
                                                        {p.label}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: C.textLight, marginTop: 3, lineHeight: 1.4 }}>{p.summary}</div>
                                                    {/* The full consequence, shown only for the chosen one —
                                                        four paragraphs at once is nobody's idea of clear. */}
                                                    {selected && (
                                                        <div style={{ fontSize: 12, color: C.textMed, marginTop: 6, lineHeight: 1.45 }}>
                                                            {p.detail}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </Section>
                        )}

                        {/* ── History ─────────────────────────────────── */}
                        {/*
                          The tier's own history. Only on edit — a tier that does not exist yet has
                          none, and the "created" row appears here the first time it is reopened.

                          This is where the concurrency guard becomes explicable rather than just
                          obstructive: an admin refused with "somebody else changed this" can open
                          this tab and see who it was and what they changed.
                        */}
                        {isEdit && tab === "history" && (
                            <ConfigAuditLog
                                entityType={CONFIG_ENTITIES.membershipType}
                                entityId={id}
                                // The tier's own merchant, not the header selection: a
                                // platform admin can be viewing one tier with "all" selected.
                                merchantId={form.merchantId || merchantId}
                            />
                        )}

                        <SaveBar dirty={dirty}>
                            <button
                                type="button"
                                onClick={() => navigate("/membership/types")}
                                style={secondaryBtnStyle}
                            >
                                Cancel
                            </button>
                            {isEdit && (
                                <button
                                    type="button"
                                    onClick={() => setForm(savedForm)}
                                    disabled={!dirty || isSaving}
                                    style={withDisabled(secondaryBtnStyle, !dirty || isSaving)}
                                >
                                    Discard
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={saveDisabled}
                                style={withDisabled(primaryBtnStyle, saveDisabled)}
                            >
                                <Save size={14} aria-hidden /> {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create tier"}
                            </button>
                        </SaveBar>
                    </Form>
                </div>
            </Container>
        </Fragment>
    );
};

export default MembershipTypeForm;
